// Pure rules for session replay recording: where it runs, what a QA card link
// looks like, and what gets stripped before anything leaves the browser.

export interface SessionReplayConfig {
  enabled: boolean;
  ingestUrl: string;
  allowedHosts: string[];
}

export type QaCardStorage = 'set' | 'remove' | 'keep';

const KAN_ID = /^KAN-\d+$/;
const META_EVENT = 4;

export const canRecord = (config: SessionReplayConfig, hostname: string): boolean =>
  config.enabled && !!config.ingestUrl && config.allowedHosts.includes(hostname);

/** `?qaCard=KAN-142` tags this tab's recording with a card; `?qaCard=off` clears it. */
export const parseQaCard = (
  search: string,
  stored: string | null,
): { card: string | null; storage: QaCardStorage } => {
  const requested = new URLSearchParams(search).get('qaCard');
  if (requested?.toLowerCase() === 'off') {
    return { card: null, storage: 'remove' };
  }
  const normalized = requested?.toUpperCase() ?? '';
  if (KAN_ID.test(normalized)) {
    return { card: normalized, storage: 'set' };
  }
  return { card: stored, storage: 'keep' };
};

/** Origin and path only: query strings and fragments can carry reset codes and invite tokens. */
export const stripUrl = (href: string): string => {
  try {
    const url = new URL(href);
    return `${url.origin}${url.pathname}`;
  } catch {
    return '';
  }
};

export const sanitizeEvent = <T extends { type: number; data?: unknown }>(event: T): T => {
  const data = event.data as { href?: string } | undefined;
  if (event.type !== META_EVENT || typeof data?.href !== 'string') {
    return event;
  }
  return { ...event, data: { ...data, href: stripUrl(data.href) } };
};

export interface RecordedRequest {
  name: string;
  method?: string;
  status?: number;
  duration?: number;
  [key: string]: unknown;
}

/** Keeps what a QA model needs (which call, did it fail, how slow) and nothing that can hold user data. */
export const sanitizeNetworkRequest = (
  request: RecordedRequest,
  ingestUrl: string,
): RecordedRequest | undefined => {
  const url = stripUrl(request.name);
  if (url === stripUrl(ingestUrl)) {
    return undefined;
  }
  const { name, method, status, duration } = request;
  const kept: RecordedRequest = { name: url || name };
  if (method !== undefined) kept.method = method;
  if (status !== undefined) kept.status = status;
  if (duration !== undefined) kept.duration = duration;
  return kept;
};

export const buildChunkUrl = (ingestUrl: string, sessionId: string, seq: number, startDate: string): string =>
  `${ingestUrl}?sid=${encodeURIComponent(sessionId)}&seq=${seq}&d=${startDate}`;
